import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Home, Settings, Zap, Thermometer, Lightbulb, Flame, 
  Camera, List, Menu, X, Activity, Shield, Cloud, Wifi,
  Lock, Wallet, Waves, DoorOpen, Gauge, HelpCircle, LogOut, User, Car, Refrigerator
} from 'lucide-react';
import { StatusLED } from './CyberComponents';
import { RoomSelector } from './NetworkAndRoom';
import { HouseSelector, useHouseSelector } from './HouseSelector';
import TopConsumersBar from './TopConsumersBar';
import { ChildLockToggle } from './ChildLock';
import { CrazyModeToggle } from './CrazyMode';
import { triggerHALClick } from './HALEyeFullscreen';
import { api } from '@/App';
import HeatingSafetyControls from './HeatingSafetyControls';
import { useAuth, AUTH_LEVELS } from './AuthContext';
import PushNotificationManager from './PushNotificationManager';

const Layout = ({ children, title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authLevel, isAdmin, isGuest, logout, hasPermission } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [time, setTime] = useState(new Date());
  const [weather, setWeather] = useState(null);
  const [network, setNetwork] = useState(null);
  const [systemStats, setSystemStats] = useState({
    cpu: 45,
    ram: 62,
    network: 38,
  });

  const halVersion = "3000"; // Version HAL - hal3000.fr
  const { currentHouse } = useHouseSelector();

  // Fonction de déconnexion
  const handleLogout = () => {
    logout();
    window.location.reload();
  };

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch weather
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const res = await api.get('/weather');
        if (res.data && !res.data.error) {
          setWeather(res.data);
        }
      } catch (e) {
        console.error('Weather fetch error:', e);
      }
    };
    fetchWeather();
    const interval = setInterval(fetchWeather, 600000); // Every 10 minutes
    return () => clearInterval(interval);
  }, []);

  // Fetch network status
  useEffect(() => {
    const fetchNetwork = async () => {
      try {
        const res = await api.get('/network/status');
        if (res.data && !res.data.error) {
          setNetwork(res.data);
        }
      } catch (e) {
        console.error('Network fetch error:', e);
      }
    };
    fetchNetwork();
    const interval = setInterval(fetchNetwork, 300000); // Every 5 minutes
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setSystemStats({
        cpu: 30 + Math.random() * 40,
        ram: 50 + Math.random() * 30,
        network: 20 + Math.random() * 50,
      });
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const menuItems = [
    { path: '/dashboard', icon: Home, label: 'ACCUEIL', color: 'cyan' },
    { path: '/security', icon: Lock, label: 'POSTE_SÉCURITÉ', color: 'cyan' },
    { path: '/energy', icon: Zap, label: 'GESTION_ÉNERGIES', color: 'cyan' },
    { path: '/thermometers', icon: Thermometer, label: 'CAPTEURS_TEMPÉRATURE', color: 'cyan' },
    { path: '/lights', icon: Lightbulb, label: 'GESTION_ÉCLAIRAGE', color: 'cyan' },
    { path: '/heating', icon: Flame, label: 'GESTION_CHAUFFAGE_ECS', color: 'cyan' },
    { path: '/vehicles', icon: Car, label: 'GESTION_VÉHICULES', color: 'blue' },
    { path: '/pool', icon: Waves, label: 'GESTION_PISCINE', color: 'cyan' },
    { path: '/gates', icon: DoorOpen, label: 'GESTION_PORTAILS', color: 'cyan' },
    { path: '/appliances', icon: Refrigerator, label: 'GESTION_ÉLECTROMÉNAGER', color: 'cyan' },
    { path: '/scenarios', icon: List, label: 'AUTOMATISMES', color: 'cyan' },
    { path: '/budget', icon: Wallet, label: 'BUDGET_ÉNERGIES', color: 'yellow' },
    { path: '/admin', icon: Shield, label: 'ADMIN', color: 'red' },
    { path: '/settings', icon: Settings, label: 'CONFIGURATION', color: 'gray' },
  ];

  return (
    <div className="flex h-screen bg-gradient-to-br from-[#0a0e27] via-[#0f1535] to-[#0a0e27] overflow-hidden hex-grid scanlines">
      {/* Sidebar Desktop */}
      <div className="hidden lg:flex lg:flex-col lg:w-52 bg-black bg-opacity-80 border-r-2 border-[#00ff9f] neon-border shadow-2xl overflow-hidden flex-shrink-0">
        {/* HAL Header with Clickable Eye */}
        <div className="flex items-center h-16 border-b-2 border-[#00ff9f] bg-gradient-to-r from-black to-[#0a0e27] px-2">
          <button 
            onClick={() => triggerHALClick()}
            className="relative flex-shrink-0 mr-2 cursor-pointer hover:scale-110 transition-transform"
            title="Parler à HAL"
            data-testid="hal-eye-sidebar"
          >
            <img 
              src="/hal-eye.gif" 
              alt="HAL" 
              className="w-10 h-10 object-contain rounded-full"
            />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-bold neon-text font-['Orbitron'] truncate">
              HAL {halVersion}
            </h1>
            {currentHouse && (
              <div className="text-[9px] text-cyan-400 font-mono truncate">[{currentHouse.name}]</div>
            )}
          </div>
          {/* CRAZY MODE Toggle - Apollo Style */}
          <CrazyModeToggle />
        </div>
        
        {/* Child Lock Toggle */}
        <ChildLockToggle />

        {/* Location Selectors - Single Box */}
        <div className="p-2 border-b border-[#00ff9f]/30">
          <div className="text-[9px] text-cyan-400 font-mono uppercase mb-1">LOCALISATION</div>
          <div className="space-y-1">
            <HouseSelector compact />
            <RoomSelector compact />
          </div>
        </div>

        {/* Météo + Réseau compact */}
        <div className="p-1.5 border-b border-[#00ff9f]/30 space-y-1">
          {weather && (
            <div className="flex items-center justify-between bg-black/30 rounded px-2 py-1">
              <span className="text-lg">{weather.weather_icon}</span>
              <span className="text-sm font-bold text-cyan-400 font-mono">{weather.temperature}°C</span>
              <span className="text-[7px] text-gray-500 font-mono max-w-[60px] truncate">{weather.weather_text}</span>
            </div>
          )}
          {network && (
            <div className="flex items-center justify-between bg-black/30 rounded px-2 py-0.5">
              <Wifi className="w-3 h-3 text-cyan-400 flex-shrink-0" />
              <span className="text-[8px] text-green-400 font-mono">{Math.round(network.ping_ms)}ms</span>
              <span className="text-[8px] text-cyan-400 font-mono">↓{network.download_mbps?.toFixed(0)}</span>
              <span className="text-[8px] text-purple-400 font-mono">↑{network.upload_mbps?.toFixed(0)}</span>
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            // Color based on item color property
            const colorClasses = {
              cyan: {
                active: 'bg-[#00ff9f]/20 text-[#00ff9f] border-l-4 border-[#00ff9f] neon-text',
                inactive: 'text-cyan-400 hover:bg-[#00ff9f]/10 hover:text-[#00ff9f]'
              },
              yellow: {
                active: 'bg-yellow-500/20 text-yellow-400 border-l-4 border-yellow-500',
                inactive: 'text-yellow-500 hover:bg-yellow-500/10 hover:text-yellow-400'
              },
              red: {
                active: 'bg-red-500/20 text-red-400 border-l-4 border-red-500',
                inactive: 'text-red-400 hover:bg-red-500/10 hover:text-red-300'
              }
            };
            
            const colors = colorClasses[item.color] || colorClasses.cyan;
            
            const handleClick = () => {
              // Protected items - use same admin code as pinpad
              if (item.protected) {
                const code = window.prompt('Code administrateur:');
                if (code !== '864886') {
                  window.alert('Code incorrect');
                  return;
                }
              }
              navigate(item.path);
            };
            
            return (
              <button
                key={item.path}
                onClick={handleClick}
                className={`w-full flex items-center px-2 py-1.5 text-left transition-all font-mono font-bold tracking-wider text-[10px] ${
                  isActive ? colors.active : colors.inactive
                } hover:border-l-4`}
              >
                <Icon className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" style={{ filter: isActive ? 'drop-shadow(0 0 5px currentColor)' : 'none' }} />
                <span className="truncate">{item.label.replace(/_/g, ' ')}</span>
                {item.protected && <Lock className="w-2.5 h-2.5 ml-auto text-red-500" />}
              </button>
            );
          })}
        </nav>

        {/* Bottom section */}
        <div className="p-1.5 border-t-2 border-[#00ff9f]/50">
          {/* Indicateur niveau d'accès */}
          <div 
            className={`mt-2 p-2 rounded border text-center ${
              isAdmin() 
                ? 'bg-green-900/30 border-green-500/50' 
                : 'bg-blue-900/30 border-blue-500/50'
            }`}
          >
            <div className="flex items-center justify-center gap-1 mb-1">
              {isAdmin() ? (
                <Shield className="w-3 h-3 text-green-400" />
              ) : (
                <User className="w-3 h-3 text-blue-400" />
              )}
              <span className={`text-[10px] font-mono font-bold ${isAdmin() ? 'text-green-400' : 'text-blue-400'}`}>
                {isAdmin() ? 'ADMIN' : 'INVITÉ'}
              </span>
            </div>
            {isGuest() && (
              <div className="text-[8px] text-gray-500 font-mono">
                Accès limité (max 21°C)
              </div>
            )}
          </div>
          
          {/* Bouton Déconnexion */}
          <button 
            onClick={handleLogout}
            className="w-full mt-2 px-2 py-1.5 bg-orange-900/30 border border-orange-500/50 rounded text-orange-400 text-[9px] font-mono font-bold hover:bg-orange-900/50 hover:border-orange-500 transition-all flex items-center justify-center gap-1"
          >
            <LogOut className="w-3 h-3" />
            DÉCONNEXION
          </button>
          
          {/* Bouton Micro Reboot Virtuel */}
          <button 
            onClick={() => {
              if (window.triggerMicroReboot) {
                window.triggerMicroReboot(); // Raison aléatoire
              }
            }}
            className="w-full mt-1 px-2 py-1.5 bg-cyan-900/30 border border-cyan-500/50 rounded text-cyan-400 text-[9px] font-mono font-bold hover:bg-cyan-900/50 hover:border-cyan-500 transition-all"
            data-testid="micro-reboot-btn"
          >
            ⟳ MICRO REBOOT
          </button>
          
          {/* Bouton Reboot Complet */}
          <button 
            onClick={() => {
              if (window.confirm('Redémarrer HAL 3000 ?')) {
                window.location.reload();
              }
            }}
            className="w-full mt-1 px-2 py-1.5 bg-red-900/30 border border-red-500/50 rounded text-red-400 text-[9px] font-mono font-bold hover:bg-red-900/50 hover:border-red-500 transition-all"
          >
            ⟳ REBOOT COMPLET
          </button>
          
          {/* Bouton Deep Reboot - Redémarre le backend */}
          <button 
            onClick={async () => {
              if (window.confirm('⚠️ DEEP REBOOT\n\nCeci va redémarrer le serveur backend.\nLa connexion sera brièvement interrompue.\n\nContinuer ?')) {
                try {
                  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
                  await fetch(`${API_URL}/api/system/deep-reboot`, { method: 'POST' });
                  alert('🔄 Deep reboot lancé !\n\nLe backend redémarre...\nRafraîchissez la page dans 10 secondes.');
                  setTimeout(() => window.location.reload(), 10000);
                } catch (e) {
                  alert('Erreur: ' + e.message);
                }
              }
            }}
            className="w-full mt-1 px-2 py-1.5 bg-purple-900/30 border border-purple-500/50 rounded text-purple-400 text-[9px] font-mono font-bold hover:bg-purple-900/50 hover:border-purple-500 transition-all"
            data-testid="deep-reboot-btn"
          >
            ⚡ DEEP REBOOT
          </button>
        </div>
      </div>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black bg-opacity-80" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-72 bg-black bg-opacity-95 border-r-2 border-[#00ff9f] neon-border">
            <div className="flex items-center justify-between h-20 px-6 border-b-2 border-[#00ff9f]">
              <h1 className="text-xl font-bold neon-text font-['Orbitron']">🏠 DOMOTIQUE</h1>
              <button onClick={() => setSidebarOpen(false)} className="text-[#00ff9f] hover:text-[#00f0ff]">
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="py-4">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center px-6 py-4 text-left transition-all font-mono font-bold ${
                      isActive
                        ? 'bg-[#00ff9f] bg-opacity-20 text-[#00ff9f] border-l-4 border-[#00ff9f]'
                        : 'text-cyan-400 hover:bg-[#00ff9f] hover:bg-opacity-10'
                    }`}
                  >
                    <Icon className="w-5 h-5 mr-3" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="flex items-center justify-between h-14 px-4 bg-black bg-opacity-80 border-b-2 border-[#00ff9f] neon-border-cyan backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-[#00ff9f] hover:text-[#00f0ff]"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-bold neon-cyan font-['Orbitron'] tracking-wider">{title}</h2>
            {/* Compact system indicators */}
            <div className="hidden md:flex items-center gap-1.5 ml-3 px-2 py-0.5 rounded bg-black/50 border border-cyan-500/20">
              <div className="flex items-center gap-1" title={`CPU: ${systemStats.cpu.toFixed(0)}%`}>
                <div className="w-1.5 h-1.5 rounded-full bg-green-500" style={{ boxShadow: '0 0 4px #00ff9f' }} />
                <span className="text-[8px] font-mono text-gray-500">CPU</span>
                <span className="text-[8px] font-mono text-green-400">{systemStats.cpu.toFixed(0)}%</span>
              </div>
              <div className="w-px h-3 bg-gray-700" />
              <div className="flex items-center gap-1" title={`RAM: ${systemStats.ram.toFixed(0)}%`}>
                <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" style={{ boxShadow: '0 0 4px #ffff00' }} />
                <span className="text-[8px] font-mono text-gray-500">RAM</span>
                <span className="text-[8px] font-mono text-yellow-400">{systemStats.ram.toFixed(0)}%</span>
              </div>
              <div className="w-px h-3 bg-gray-700" />
              <div className="flex items-center gap-1" title={`NET: ${systemStats.network.toFixed(0)}%`}>
                <div className="w-1.5 h-1.5 rounded-full bg-fuchsia-500" style={{ boxShadow: '0 0 4px #ff00ff' }} />
                <span className="text-[8px] font-mono text-gray-500">NET</span>
                <span className="text-[8px] font-mono text-fuchsia-400">{systemStats.network.toFixed(0)}%</span>
              </div>
            </div>
            {/* Mode d'emploi button */}
            <button
              onClick={() => {
                alert(`📖 MODE D'EMPLOI HAL 3000\n\n` +
                  `🔒 VERROU - Bloque tous les chauffages\n` +
                  `🏠 MAISON VIDE - Désactive le chauffage automatiquement\n` +
                  `🎭 CRAZY MODE - Active/désactive les alertes ludiques\n` +
                  `💡 ÉCLAIRAGES - Cliquez sur un interrupteur ou TOUT ON/OFF\n` +
                  `🌡️ CAPTEURS - Couleurs: Bleu <12°, Vert <16°, Orange 16-25°, Rouge >25°\n` +
                  `⚙️ CONFIGURATION - Code: 864886\n\n` +
                  `🔥 POÊLE À PELLETS - En attente de clé API\n` +
                  `📊 ÉNERGIE - Production solaire + consommation Enedis`);
              }}
              className="p-1.5 rounded-full bg-cyan-900/30 border border-cyan-500/50 text-cyan-400 hover:bg-cyan-900/50 hover:text-cyan-300 transition-all"
              title="Mode d'emploi"
              data-testid="help-button"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center space-x-3 text-xs font-mono">
            {/* Top consumers ticker - visible toutes pages */}
            <div className="hidden md:block max-w-[520px]">
              <TopConsumersBar currentHouse={currentHouse} limit={3} />
            </div>
            {/* Heating Safety Controls - Always visible */}
            <HeatingSafetyControls compact />
            <PushNotificationManager compact />
            <div className="text-[#00f0ff]">
              {time.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
            </div>
            <div className="text-[#00ff9f] font-bold neon-text">
              {time.toLocaleTimeString('fr-FR')}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-hidden p-3 relative" style={{ height: 'calc(100vh - 56px)' }}>
          <div className="w-full h-full overflow-hidden">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;